import type { DataOperationRealEstateFinancingFormData } from '@/components/Forms/DataOperationRealEstateFinancingForm';
import {
	MAX_FINANCIAL_TERM_COMMERCIAL,
	MAX_FINANCIAL_TERM_RESIDENTIAL,
	MAX_RENT_VALUE,
	MIN_FINANCIAL_TERM,
	MIN_RENT_VALUE,
	Messages,
	PropertyKind,
	PropertyType,
} from '@/constants';
import { EmailValidationContext } from '@/constants/EmailValidationContext';
import { ClientService } from '@/features/Clients/ClientService';
import { validationService } from '@/services/validationService';
import type { FormItemRule } from 'naive-ui/es/form';
import { Validations } from './validations';

export class FormValidations {
	public static IsRequired: FormItemRule = {
		required: true,
		message: Messages.ERROR_IS_REQUIRED,
		validator(rule: FormItemRule, value: string | number | boolean) {
			if (value === null || value === undefined || value === '')
				return new Error(Messages.ERROR_IS_REQUIRED);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsChoose: FormItemRule = {
		required: true,
		message: Messages.ERROR_IS_SELECT_REQUIRED,
		validator(rule: FormItemRule, value: string | number | boolean) {
			if (value === null || value === undefined || value === '')
				return new Error(Messages.ERROR_IS_SELECT_REQUIRED);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsSelectRequired: FormItemRule = {
		required: true,
		message: Messages.ERROR_IS_REQUIRED,
		validator(rule: FormItemRule, value: string | number | boolean) {
			if (value === null || value === undefined || value === '')
				return new Error(Messages.ERROR_IS_SELECT_REQUIRED);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsFileRequired: FormItemRule = {
		required: true,
		message: Messages.ERROR_IS_FILE_REQUIRED,
		validator(rule: FormItemRule, value: string | number | boolean) {
			if (value == null || value == undefined || !value)
				return new Error(Messages.ERROR_IS_FILE_REQUIRED);

			return true;
		},
		trigger: ['blur', 'input'],
	};
	public static IsDateGreaterThanYesterday: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (!Validations.isDateGreaterThanYesterday(value))
				return new Error(Messages.ERROR_DATE_GREATER_THAN_YESTERDAY);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	static MinimalNameLength: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			// Campo vazio é problema do IsRequired, não deste validator. Sem esta guarda,
			// `undefined.split` LANÇA dentro do validator: o naive-ui engole o erro, a promise de
			// form.validate() NUNCA resolve, e quem espera por ela (o `Promise.all` do passo 1 da
			// Garantia) fica pendurado com o botão Continuar travado em silêncio.
			if (!value) return true;

			// Regra do ClickSign, medida no sandbox: nome e sobrenome separados por espaço comum e
			// nenhum dígito. Fora dela, o envio para assinatura é recusado lá. Bordas não contam.
			if (value.trim().split(' ').filter(Boolean).length <= 1)
				return new Error(Messages.ERROR_NAME_MINIMAL_LENGTH);
			if (/[0-9]/.test(value)) return new Error(Messages.ERROR_NAME_WITH_DIGITS);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsRequiredIf(object: any, inputLabel: string, compareValue: any): FormItemRule {
		let value: any;
		let required = true;

		if (inputLabel in object) {
			value = object[inputLabel];
		}

		required = value == compareValue;

		return {
			required: required,
			message: Messages.ERROR_IS_REQUIRED,
			validator(rule: FormItemRule, value: string | number | boolean) {
				if (!required) return true;

				if (value == null || value == undefined) return new Error(Messages.ERROR_IS_REQUIRED);

				return true;
			},
			trigger: ['blur', 'input'],
		};
	}

	public static IsEmailValid: FormItemRule = {
		required: true,
		validator(rule: FormItemRule, value: string) {
			if (!Validations.validateEmail(value)) return new Error(Messages.ERROR_INVALID_EMAIL);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	/**
	 * Regra de validação assíncrona genérica para checar se um e-mail está disponível.
	 * @param context O contexto da validação (Broker, Renter, User)
	 * @param originalEmail O e-mail original do formulário (para pular a checagem se não mudou)
	 */
	public static IsEmailAvailable(
		context: EmailValidationContext,
		originalEmail?: string,
		idToIgnore?: string,
	): FormItemRule {
		return {
			async validator(rule: FormItemRule, value: string) {
				if (!value) return;
				if (value.toLowerCase() === originalEmail?.toLowerCase()) {
					return;
				}
				try {
					await validationService.isEmailAvailable(value, context, idToIgnore);
					return;
				} catch (error) {
					throw new Error(Messages.ERROR_EMAIL_ALREADY_IN_USE, { cause: error });
				}
			},
			trigger: ['blur'],
		};
	}

	public static IsCpfOrCnpj: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (!Validations.validateCnpj(value) && !Validations.validateCpf(value))
				return new Error(Messages.ERROR_INVALID_DOCUMENT);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsCpfValid: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (!Validations.validateCpf(value)) return new Error(Messages.ERROR_INVALID_DOCUMENT);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static NoDuplicates(list: string[], message: string): FormItemRule {
		return {
			validator(rule: FormItemRule, value: string) {
				if (list.includes(value)) return new Error(message);

				return true;
			},
			trigger: ['blur', 'input'],
		};
	}

	public static NoDuplicatesNormalized(
		list: string[],
		message: string,
		comparisonMode: 'exact' | 'caseInsensitive' | 'numericOnly' = 'exact',
	): FormItemRule {
		return {
			validator(rule: FormItemRule, value: string) {
				if (!value) return true;

				const normalize = (val: string) => {
					if (!val) return '';
					const strVal = String(val);

					switch (comparisonMode) {
						case 'caseInsensitive':
							return strVal.trim().toLowerCase();
						case 'numericOnly':
							return strVal.replace(/\D/g, '');
						default:
							return strVal;
					}
				};

				const normalizedValue = normalize(value);
				const isDuplicate = list.some((item) => normalize(item) === normalizedValue);
				if (isDuplicate) return new Error(message);

				return true;
			},
			trigger: ['blur', 'input'],
		};
	}

	public static IsCnpjValid: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (!Validations.validateCnpj(value)) return new Error(Messages.ERROR_INVALID_DOCUMENT);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsGreaterThanZero: FormItemRule = {
		validator(rule: FormItemRule, value: number | string) {
			if (typeof value === 'string') value = Number(value);

			if (value <= 0) return new Error(Messages.ERROR_NUMBER_GREATER_THAN_ZERO);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsDateValid: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (!Validations.isDateValid(value)) return new Error(Messages.ERROR_INVALID_DATE);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static validateAluguelDate(value: string): boolean | string {
		if (!value) return true;

		const [year, month, day] = value.split('-').map(Number);
		if (!year || !month || !day) return true;

		const dueDate = new Date(year, month - 1, day);
		dueDate.setHours(0, 0, 0, 0);

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const diffTime = today.getTime() - dueDate.getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

		if (diffDays < 15 || diffDays > 45) {
			return 'Uma ocorrência de aluguel só pode ser aberta entre 15 e 45 dias após o vencimento do boleto.';
		}

		return true;
	}

	public static IsAluguelDateValid: FormItemRule = {
		trigger: ['blur', 'change'],
		validator(rule: FormItemRule, value: string) {
			const result = FormValidations.validateAluguelDate(value);

			if (typeof result === 'string') {
				return new Error(result);
			}
			return true;
		},
	};

	public static IsOver18YearsOld: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (!Validations.isOver18YearsOld(value)) return new Error(Messages.ERROR_OVER_18_YEARS_OLD);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsOver65YearsOld: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (Validations.isOver65YearsOld(value)) return new Error(Messages.ERROR_OVER_65_YEARS_OLD);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsMinimalOpeningDateValid: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (!Validations.isMinimalOpeningDate(value))
				return new Error(Messages.ERROR_MINIMAL_OPENING_DATE);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsScoreValid: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			const score = parseInt(value);
			if (!(score >= 0 && score <= 1000)) return new Error(Messages.ERROR_SCORE_BETWEEN_0_AND_1000);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsNameFullFilled: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (!Validations.validateFullname(value)) return new Error(Messages.ERROR_INVALID_NAME);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsArrayFilled: FormItemRule = {
		validator(rule: FormItemRule, value: any[]) {
			if (!Validations.isArrayFullFilled(value))
				return new Error(Messages.ERROR_INVALID_ARRAY_IS_EMPTY);

			return true;
		},
		trigger: ['blur', 'input'],
	};

	public static IsTermValid = (
		data: DataOperationRealEstateFinancingFormData,
		birhdate: string,
	): FormItemRule => {
		return {
			validator(rule: FormItemRule, value: number) {
				let maxTerm = Validations.validateMaxFinancingTerm(birhdate, value);
				const baseTerm =
					data.propertyPorpouse == PropertyType.Commercial
						? MAX_FINANCIAL_TERM_COMMERCIAL
						: MAX_FINANCIAL_TERM_RESIDENTIAL;

				if (maxTerm > baseTerm) maxTerm = baseTerm;

				if (maxTerm < 0) return new Error(Messages.ERROR_TERM_BUYER_OVER_LIMIT);
				if (value < MIN_FINANCIAL_TERM) return new Error(Messages.ERROR_TERM_MIN);
				if (value > maxTerm) return new Error(Messages.ERROR_TERM_MAX(maxTerm));

				return true;
			},
			trigger: ['blur', 'input'],
		};
	};

	public static IsOwnResourcesValueValid = (
		data: DataOperationRealEstateFinancingFormData,
	): FormItemRule => {
		return {
			validator(rule: FormItemRule, value: number) {
				const sumOwnResource = data.ownResourceFgtsValue + data.ownResourceValue;
				if (sumOwnResource >= data.propertyValue)
					return new Error(Messages.ERROR_FINANCING_VALUE_SHOULD_BE_LOWER);
				if (sumOwnResource == 0) return new Error(Messages.ERROR_FINANCING_OWN_RESOURCES_REQUIRED);
				const minimumFinancingValue = data.propertyValue * 0.1;
				if (sumOwnResource < minimumFinancingValue)
					return new Error(Messages.ERROR_FINANCING_VALUE_SHOULD_BE_GREATER(minimumFinancingValue));

				return true;
			},
			trigger: ['change', 'blur', 'input'],
		};
	};

	public static IsFinancingValueValid = (
		data: DataOperationRealEstateFinancingFormData,
	): FormItemRule => {
		return {
			validator(rule: FormItemRule, value: number) {
				// Check if the financing value is less than the minimum
				const minimumFinancingValue = data.propertyValue * 0.1;

				if (data.financingValue < minimumFinancingValue)
					return new Error(Messages.ERROR_FINANCING_VALUE_MIN(minimumFinancingValue));

				return true;
			},
			trigger: ['change', 'blur', 'input'],
		};
	};

	public static MinLength(minLength: number): FormItemRule {
		return {
			validator(rule: FormItemRule, value: string) {
				if (value?.length < minLength) return new Error(Messages.ERROR_MIN_LENGTH(minLength));

				return true;
			},
			trigger: ['change', 'blur', 'input'],
		};
	}

	public static IsEqual(aValue: string, bValue: string): FormItemRule {
		return {
			validator(rule: FormItemRule, value: string) {
				if (aValue !== bValue) return new Error(Messages.ERROR_IS_EQUAL('senha'));

				return true;
			},
			trigger: ['change', 'blur', 'input'],
		};
	}

	public static PasswordPolicy: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			const regex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*]).{8,}$');

			if (!regex.test(value)) return new Error(Messages.ERROR_PASSWORD_POLICY);

			return true;
		},
	};

	public static IsMobileValid: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (!value) return true; // Deixa o IsRequired lidar com campo vazio
			if (!Validations.validateMobile(value)) return new Error(Messages.ERROR_INVALID_PHONE);
			return true;
		},
		trigger: ['blur', 'input'],
	};

	// Regra Estática para Fixo
	public static IsLandlineValid: FormItemRule = {
		validator(rule: FormItemRule, value: string) {
			if (!value) return true;
			if (!Validations.validateLandline(value)) return new Error(Messages.ERROR_INVALID_PHONE);
			return true;
		},
		trigger: ['blur', 'input'],
	};

	/**
	 * Regra Dinâmica: Valida baseada no tipo passado ('Cel' ou 'Tel')
	 * Útil se o tipo de telefone vier de uma prop ou estado do componente pai
	 */
	public static IsPhoneValid(type: 'Cel' | 'Tel'): FormItemRule {
		return {
			validator(rule: FormItemRule, value: string) {
				if (!value) return true;

				const isValid =
					type === 'Cel' ? Validations.validateMobile(value) : Validations.validateLandline(value);

				if (!isValid) return new Error(Messages.ERROR_INVALID_PHONE);

				return true;
			},
			trigger: ['blur', 'input'],
		};
	}

	public static IsPhoneAvailable(
		originalPhone?: string,
		onConflict?: (info: {
			phone: string;
			ownerId?: string;
			ownerName?: string;
			ownerDocumentMasked?: string;
			ownerPhone?: string;
		}) => void,
		idToIgnore?: string,
	): FormItemRule {
		return {
			async validator(rule: FormItemRule, value: string) {
				if (!value) return;
				// Se o telefone não mudou do original carregado, não precisa checar no banco
				if (originalPhone && value.replace(/\D/g, '') === originalPhone.replace(/\D/g, '')) {
					return;
				}
				try {
					await ClientService.validateClientPhone(value, idToIgnore);
					return;
				} catch (error) {
					if (onConflict) {
						try {
							const lookup = await ClientService.getRenterByPhone(value);
							const owner = lookup?.data;
							if (owner) {
								onConflict({
									phone: value,
									ownerId: owner.id,
									ownerName: owner.name,
									ownerDocumentMasked: owner.documentMasked,
									ownerPhone: owner.phone,
								});
							} else {
								onConflict({ phone: value });
							}
						} catch {
							onConflict({ phone: value });
						}
					}
					throw Error(Messages.ERROR_PHONE_ALREADY_IN_USE, { cause: error });
				}
			},
			trigger: ['blur', 'change'],
		};
	}

	public static isBrokerPending(object: any, inputLabel: string, compareValue: any): FormItemRule {
		let value: any;
		let required = true;

		if (inputLabel in object) {
			value = object[inputLabel];
		}

		required = value == compareValue;

		return {
			required: required,
			message: Messages.ERROR_BROKER_PENDING,
			validator(rule: FormItemRule, value: string | number | boolean) {
				if (!required) return true;

				if (value == compareValue) return new Error(Messages.ERROR_BROKER_PENDING);

				return true;
			},
			trigger: ['blur', 'input'],
		};
	}

	/**
	 * Faixa de aluguel que a Almada garante (ENG-588): R$ 500 a R$ 20.000, limites INCLUSIVOS.
	 *
	 * Mede o aluguel PURO. Despesas adicionais, IPTU e condomínio somam no Total Pacote e são a base
	 * da garantia, mas ficam de fora da faixa — foi a decisão do negócio.
	 *
	 * Vale sempre que o campo for editado — para a aba do admin, que edita contrato já existente,
	 * use a `IsRentValueWithinGuaranteedLimitsWhenChanged`.
	 *
	 * NÃO usar na renovação: lá a faixa avisa, não barra.
	 */
	public static IsRentValueWithinGuaranteedLimits: FormItemRule = {
		validator(rule: FormItemRule, value: number | string) {
			return FormValidations.ResolveRentValueRangeError(value);
		},
		trigger: ['blur', 'input'],
	};

	/**
	 * A mesma faixa, cobrada apenas quando o aluguel MUDA em relação ao que está gravado.
	 *
	 * É para a aba "Valores" do admin, que edita contrato já existente. Contrato assinado antes da
	 * regra pode estar legitimamente fora da faixa, e travar o formulário ali impediria a correção
	 * de qualquer outro campo — setup, datas, forma de pagamento — por causa de um aluguel que
	 * ninguém encostou. Espelha o recorte que o `QuotationService.UpdateQuotationAsync` faz no
	 * servidor; sem ele a tela barraria o que a API passou a aceitar.
	 *
	 * Continua barrando quem MOVE o aluguel para fora da faixa.
	 *
	 * @param originalRentValue Aluguel gravado quando o formulário abriu. Nulo/indefinido faz a
	 * regra valer sempre — é o lado seguro para quem não conseguir informar o valor de origem.
	 */
	public static IsRentValueWithinGuaranteedLimitsWhenChanged(
		originalRentValue: number | null | undefined,
	): FormItemRule {
		return {
			validator(rule: FormItemRule, value: number | string) {
				if (originalRentValue !== null && originalRentValue !== undefined) {
					const rentValue = typeof value === 'string' ? Number(value) : value;
					if (rentValue === originalRentValue) return true;
				}

				return FormValidations.ResolveRentValueRangeError(value);
			},
			trigger: ['blur', 'input'],
		};
	}

	/**
	 * Núcleo da faixa, compartilhado pelas duas regras acima para não existirem dois lugares com os
	 * limites e as mensagens.
	 *
	 * Deixa `null`/`undefined` passar de propósito: o `AppInputMoney` emite `undefined` quando o
	 * campo é limpo, e quem manda em campo vazio é o `IsRequired`. Sem esse passthrough, apagar o
	 * valor mostraria "o mínimo é R$ 500" em vez de "preencha este campo".
	 */
	private static ResolveRentValueRangeError(value: number | string): true | Error {
		if (value === null || value === undefined || value === '') return true;

		const rentValue = typeof value === 'string' ? Number(value) : value;
		if (Number.isNaN(rentValue)) return true;

		if (rentValue < MIN_RENT_VALUE) return new Error(Messages.ERROR_RENT_VALUE_BELOW_MINIMUM);
		if (rentValue > MAX_RENT_VALUE) return new Error(Messages.ERROR_RENT_VALUE_ABOVE_MAXIMUM);

		return true;
	}

	/**
	 * Valida se um valor numérico é menor ou igual a um valor máximo.
	 * @param maxValue O valor máximo permitido.
	 * @param errorMessage A mensagem de erro a ser exibida.
	 */
	public static IsValueLessThan(maxValue: number, errorMessage: string): FormItemRule {
		return {
			validator(rule: FormItemRule, value: number) {
				if (value === null || value === undefined) {
					return true;
				}

				if (value > maxValue) {
					return new Error(errorMessage);
				}

				return true;
			},
			trigger: ['blur', 'input'],
		};
	}

	public static IsAdditionalRequired(propertyKind: PropertyKind, additional: string): FormItemRule {
		let required = true;

		required =
			propertyKind === PropertyKind.Apartment ||
			propertyKind === PropertyKind.Loft ||
			propertyKind === PropertyKind.Studio ||
			propertyKind === PropertyKind.CommercialStore ||
			propertyKind === PropertyKind.CommercialOffice;
		return {
			required: required,
			message: Messages.ERROR_IS_REQUIRED,
			validator(rule: FormItemRule, value: string | number | boolean) {
				if (!required) return true;

				if (additional == null || additional == undefined)
					return new Error(Messages.ERROR_IS_REQUIRED);

				return true;
			},
			trigger: ['blur', 'input'],
		};
	}
}
